import axios from 'axios';

const API = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api',
  timeout: 60000, // Increased to 60s for Render cold starts
  withCredentials: false, // Not needed since we use header-based JWT auth
  headers: {
    'Content-Type': 'application/json',
  }
});

const NON_REDIRECT_401_PATHS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/change-password',
  '/auth/google',
]);

// Attach JWT token
API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('nb_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Handle 401
API.interceptors.response.use(
  res => res,
  err => {
    const requestPath = err.config?.url || '';
    const hasToken = Boolean(localStorage.getItem('nb_token'));
    if (
      err.response?.status === 401 &&
      hasToken &&
      !NON_REDIRECT_401_PATHS.has(requestPath)
    ) {
      localStorage.removeItem('nb_token');
      localStorage.removeItem('nb_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (err.code === 'ECONNABORTED' && !err.response) {
      err.message = 'The server took too long to respond. Please make sure the backend is running and try again.';
    }

    return Promise.reject(err);
  }
);

let publicSettingsCache = null;
let publicSettingsCacheTs = 0;
let publicSettingsPromise = null;
const PUBLIC_SETTINGS_TTL_MS = 5 * 60 * 1000;

function getPublicSettingsCached(options = {}) {
  const { force = false } = options;
  const now = Date.now();

  if (!force && publicSettingsCache && now - publicSettingsCacheTs < PUBLIC_SETTINGS_TTL_MS) {
    return Promise.resolve(publicSettingsCache);
  }

  if (!force && publicSettingsPromise) {
    return publicSettingsPromise;
  }

  publicSettingsPromise = API.get('/auth/public-settings')
    .then((res) => {
      publicSettingsCache = res;
      publicSettingsCacheTs = Date.now();
      return res;
    })
    .finally(() => {
      publicSettingsPromise = null;
    });

  return publicSettingsPromise;
}

export const authAPI = {
  register: (data) => API.post('/auth/register', data),
  login: (data) => API.post('/auth/login', data),
  me: () => API.get('/auth/me'),
  referralProgram: () => API.get('/auth/referral-program'),
  updateProfile: (data) => API.patch('/auth/profile', data),
  changePassword: (data) => API.post('/auth/change-password', data),
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPassword: (data) => API.post('/auth/reset-password', data),
  google: (data) => API.post('/auth/google', data),
  publicSettings: (options) => getPublicSettingsCached(options),
};

export const servicesAPI = {
  getAll: () => API.get('/services'),
  getById: (id) => API.get(`/services/${id}`),
  adminAll: () => API.get('/services/admin/all'),
  adminSync: () => API.post('/services/admin/sync'),
  adminUpdate: (id, data) => API.patch(`/services/${id}`, data),
};

export const ordersAPI = {
  place: (data) => API.post('/orders', data),
  getAll: (page = 1) => API.get(`/orders?page=${page}`),
  getRecent: () => API.get('/orders/recent'),
  adminAll: (page = 1) => API.get(`/orders/admin/all?page=${page}`),
  adminRecent: () => API.get('/orders/admin/recent'),
  adminRefund: (id, amount) => API.post(`/orders/${id}/refund`, { amount }),
};

export const depositsAPI = {
  rateInfo: () => API.get('/deposits/rate-info'),
  initiateFapshi: (amount_xaf) => API.post('/deposits/fapshi/initiate', { amount_xaf }),
  initiateHelaket: (amount, currency) => API.post('/deposits/helaket/initiate', { amount, currency }),
  initiateMonetbil: (amount_xaf, phone = '') => API.post('/deposits/monetbil/initiate', { amount_xaf, phone }),
  initiateSwychr: (payload) => API.post('/deposits/swychr/initiate', payload),
  verifyFapshi: (depositId) => API.get(`/deposits/fapshi/verify/${depositId}`),
  verifyMonetbil: (depositId) => API.get(`/deposits/monetbil/verify/${depositId}`),
  verifySwychr: (depositId) => API.get(`/deposits/swychr/verify/${depositId}`),
  history: () => API.get('/deposits/history'),
  adminAll: () => API.get('/deposits/admin/all'),
  adminMarkVerified: (depositId) => API.post(`/deposits/admin/${depositId}/mark-verified`),
};

export const adminAPI = {
  stats: () => API.get('/admin/stats'),
  users: () => API.get('/admin/users'),
  updateUser: (id, data) => API.patch(`/admin/users/${id}`, data),
  settings: () => API.get('/admin/settings'),
  updateSettings: (data) => API.patch('/admin/settings', data),
  transactions: () => API.get('/admin/transactions'),
};

export const supportAPI = {
  create: (data) => API.post('/support', data),
  mine: () => API.get('/support/mine'),
  reply: (id, data) => API.post(`/support/${id}/messages`, data),
  adminAll: () => API.get('/support/admin/all'),
  adminUpdate: (id, data) => API.patch(`/support/admin/${id}`, data),
};

export const serviceTestingAPI = {
  adminTesting: () => API.get('/service-testing/admin/testing'),
  adminTestingDetail: (id) => API.get(`/service-testing/admin/testing/${id}`),
  assignTesters: (id, tester_ids) => API.post(`/service-testing/admin/assign-testers/${id}`, { tester_ids }),
  submitFeedback: (id, status, feedback) => API.post(`/service-testing/${id}/feedback`, { status, feedback }),
  approve: (id) => API.post(`/service-testing/admin/approve/${id}`),
  reject: (id, reason) => API.post(`/service-testing/admin/reject/${id}`, { reason }),
  myTesting: () => API.get('/service-testing/my-testing'),
};

export const virtualNumbersAPI = {
  getCountries:   () => API.get('/virtual-numbers/countries'),
  getServices:    () => API.get('/virtual-numbers/services'),
  getPrices:      (serviceCode) => API.get(`/virtual-numbers/prices?service=${serviceCode}`),
  getOrders:      () => API.get('/virtual-numbers/orders'),
  checkOrder:     (orderId) => API.get(`/virtual-numbers/order/${orderId}`),
  purchaseNumber: (countryId, serviceCode, countryName, serviceName) =>
    API.post('/virtual-numbers/order', {
      country: countryId,
      service: serviceCode,
      country_name: countryName,
      service_name: serviceName,
    }),
  cancelOrder:    (orderId) => API.post(`/virtual-numbers/order/${orderId}/cancel`),
};

export const notificationsAPI = {
  getNotifications: () => API.get('/notifications'),
  markAsRead: (id) => API.patch(`/notifications/${id}/read`),
  markAllAsRead: () => API.patch('/notifications/read-all'),
};

export default API;
